import { fetchDMContent } from "./Api";
import { markdownToHtml } from "../common/Markdown";
import { DMItemWithOwner, getDMImageUrl, getDMOwnerName, solrQuote, solrSelect, toDMItem } from "./DigitaltMuseum";

/*
 * DigitaltMuseum articles (subtype "article"). The kiosk rebuilds them from the article data in DM's native
 * artifact API (`_content` from DM's article editor), instead of showing the DM page in an iframe.
 */

/** An image in an article. DM objects used as images have a uniqueId */
export interface DMArticleImage {
  url: string,
  caption?: string,
  credit?: string,
  uniqueId?: string,
  /** Main color of the image, from DM */
  bgColor?: string
}

/** A block in an article, simplified from DM's article editor */
export type DMArticleBlock =
  | { type: "text", title?: string, html: string, images: DMArticleImage[], imagePosition?: "left" | "right" }
  | { type: "media", title?: string, html: string, images: DMArticleImage[], layout: "grid" | "slideshow" }
  | { type: "objects", title?: string, html: string, items: DMItemWithOwner[] };

/** An article from DM (subtype "article") */
export interface DMArticle {
  title: string,
  /** Lead text as HTML */
  leadHtml?: string,
  author?: string,
  image?: DMArticleImage,
  ownerName?: string,
  blocks: DMArticleBlock[]
}

/** Convert an image or DM object in an article's mediaList */
const toArticleImage = (media: any): DMArticleImage | null => {
  const url = media?.url || (media?.dms_id && getDMImageUrl(media.dms_id));
  if (!url || !["image", "DM"].includes(media?.type)) { return null; }
  return {
    url,
    caption: media.local?.description || (media.type === "DM" ? media.title : undefined) || undefined,
    credit: media.local?.credit || undefined,
    uniqueId: media.type === "DM" ? media.dimuId : undefined,
    bgColor: media.bgColor || undefined
  };
}

const toArticleImages = (block: any): DMArticleImage[] => (block?.resources?.mediaList || []).map(toArticleImage).filter(Boolean);

/** Sorts used in DM's article searches, and the Solr field they sort on */
const ARTICLE_SEARCH_SORTS: Record<string, string> = {
  relevance: "score",
  published: "artifact.publishedDate",
  updated: "artifact.updatedDate",
  identifier: "identifier.id",
  title: "artifact.ingress.title"
};

/** Max number of objects shown from one search in an article */
const ARTICLE_SEARCH_MAX_ROWS = 40;

interface ArticleSearch {
  text: string,
  owner?: string,
  type?: string,
  sort?: string,
  sortOrder?: string,
  rows?: number
}

/**
 * Run a search from an article (an object list defined as a DM search) and return the objects
 * Free text uses OR between the terms, like DM's own search
 */
const runArticleSearch = async (search: ArticleSearch): Promise<DMItemWithOwner[]> => {
  const type = search.type && search.type.charAt(0).toUpperCase() + search.type.slice(1);
  const sortField = ARTICLE_SEARCH_SORTS[search.sort || "relevance"] || "score";
  const data = await solrSelect({
    q: search.text.trim() || "*",
    "q.op": "OR",
    fq: [search.owner && `identifier.owner:${solrQuote(search.owner)}`, type && `artifact.type:${solrQuote(type)}`].filter(Boolean) as string[],
    sort: `${sortField} ${search.sortOrder === "asc" ? "asc" : "desc"}`,
    rows: Math.min(search.rows || 8, ARTICLE_SEARCH_MAX_ROWS)
  }).catch(() => null);
  return (data?.response?.docs || []).map((doc: any) => ({ ...toDMItem(doc), title: getArticleCardTitle(doc?.["artifact.ingress.title"], doc?.["artifact.ingress.description"]) }));
}

/**
 * Text on object cards in articles, like on DigitaltMuseum: the description, prefixed with the title
 * ("Såpe. To såper i brun farge …") unless the description already starts with it
 */
const getArticleCardTitle = (title?: string, description?: string): string | undefined => {
  if (!description) { return title; }
  if (!title || description.toLowerCase().startsWith(title.toLowerCase())) { return description; }
  return `${title}. ${description}`;
}

/**
 * Parse a search entry from DM's article editor. `query` holds free text and key:value pairs
 * (e.g. `"Walhalla"+"Freyasdal" owner:VAMF type:thing rows:4`), `filter` the same values as an object.
 * Other key:value pairs (e.g. classification) are searched as text.
 */
const parseArticleSearchEntry = (entry: any): ArticleSearch => {
  const values: Record<string, string> = {};
  const text = String(entry?.query || "").replace(/\+/g, " ").replace(/(\w+):("[^"]*"|\S+)/g, (match, key, value) => {
    if (["owner", "type", "sort", "sort_order", "rows"].includes(key)) {
      values[key] = value.replace(/"/g, "");
      return "";
    }
    return value;
  });
  const filter = entry?.filter || {};
  return {
    text,
    owner: filter.owner || values.owner,
    type: filter.type || values.type,
    sort: filter.sort || values.sort,
    sortOrder: filter.sort_order || values.sort_order,
    rows: Number(filter.rows || values.rows) || undefined
  };
}

/** Parse a DM search URL (digitaltmuseum.org/search?q=...&owner=...&type=...) */
const parseArticleSearchUrl = (url: string, rows?: number): ArticleSearch | null => {
  try {
    const params = new URL(url, "https://digitaltmuseum.org").searchParams;
    return {
      text: [params.get("q"), params.get("producer")].filter(Boolean).join(" "),
      owner: params.get("owner") || undefined,
      type: params.get("type") || undefined,
      sort: params.get("sort") || undefined,
      sortOrder: params.get("sort_order") || undefined,
      rows
    };
  } catch (e) {
    return null;
  }
}

/**
 * Get the objects in a DigitaltMuseum block of an article, in the order of the article.
 * Entries are single objects ("DM") or searches ("search"). Older blocks only have a search URL
 */
const getArticleObjects = async (block: any): Promise<DMItemWithOwner[]> => {
  const entries: any[] = block?.resources?.entryList || [];
  const searchFromUrl = entries.length === 0 && block?.searchUrl ? parseArticleSearchUrl(block.searchUrl, Number(block.maxListItems) || undefined) : null;

  const lists = await Promise.all([
    ...entries.map(async (entry): Promise<DMItemWithOwner[]> => {
      if (entry?.type === "search") { return runArticleSearch(parseArticleSearchEntry(entry)); }
      return entry?.dimuId ? [{
        uniqueId: entry.dimuId,
        uuid: entry.uuid,
        // DM shows the description on the cards in articles
        title: entry.local?.description || entry.description || entry.title,
        type: entry.artifactType,
        ownerCode: entry.owner_identifier,
        ownerName: entry.owner_name,
        mediaIdentifier: entry.image_id || undefined
      }] : [];
    }),
    ...(searchFromUrl ? [runArticleSearch(searchFromUrl)] : [])
  ]);

  const items = lists.flat();

  // Picked articles have no subtype, which tells articles, virtual experiences and school programmes apart
  const missing = items.filter(item => !item.subtype && (!item.type || item.type === "Article")).map(item => item.uniqueId);
  const subtypes: Record<string, any> = {};
  if (missing.length > 0) {
    const data = await solrSelect({ q: "*", fq: `artifact.uniqueId:(${missing.map(solrQuote).join(" OR ")})`, rows: missing.length }).catch(() => null);
    (data?.response?.docs || []).forEach((doc: any) => { subtypes[doc["artifact.uniqueId"]] = doc; });
  }

  // Objects from searches have no owner name
  return Promise.all(items.map(async item => ({
    ...item,
    subtype: item.subtype || subtypes[item.uniqueId]?.["artifact.subtype"],
    uuid: item.uuid || subtypes[item.uniqueId]?.["artifact.uuid"],
    ownerName: item.ownerName || (item.ownerCode ? await getDMOwnerName(item.ownerCode, item.uniqueId) : undefined)
  })));
}

/**
 * Get an article from DM's native artifact API (`_content` from DM's article editor), simplified to the
 * blocks the kiosk shows: text (optionally with an image beside it), images, and lists of DM objects
 * @param {string} uuid DM artifact UUID
 * @returns {DMArticle} Article
 */
export const fetchDMArticle = async (uuid: string): Promise<DMArticle> => {
  const data = await fetchDMContent(`https://api.dimu.org/artifact/uuid/${uuid}`);
  const blocks: any[] = (data?._content?.sections || []).flatMap((section: any) => section?.blocks || []);
  const head = blocks.find(block => block?.type === "article__content");

  const articleBlocks = (await Promise.all(blocks
    .filter(block => block?.settings?.visibility !== "hidden")
    .map(async (block): Promise<DMArticleBlock | null> => {
      const title = !block.titleHidden && block.title ? block.title : undefined;
      const html = markdownToHtml(block.bodytext);
      switch (block.type) {
        case "article__bodytext": {
          const position = { "img-left": "left", "img-right": "right" }[block.settings?.layout as string] as "left" | "right" | undefined;
          return { type: "text", title, html, images: toArticleImages(block), imagePosition: position };
        }
        case "article__media":
          return { type: "media", title, html, images: toArticleImages(block), layout: block.settings?.layout === "slideshow" ? "slideshow" : "grid" };
        case "digitaltMuseum":
          return { type: "objects", title, html, items: await getArticleObjects(block) };
        default:
          return null;
      }
    })))
    .filter((block): block is DMArticleBlock => Boolean(block) && (block!.type === "objects" ? block!.items.length > 0 : Boolean(block!.title || block!.html || block!.images.length)));

  return {
    title: head?.title || data?.title || "",
    leadHtml: head?.leadtext ? markdownToHtml(head.leadtext) : undefined,
    author: head?.author || undefined,
    // The main image's color is also in media.pictures
    image: (image => image && {
      ...image,
      bgColor: image.bgColor || data?.media?.pictures?.find((picture: any) => image.url.endsWith(picture?.identifier))?.color
    })(toArticleImages(head)[0]),
    ownerName: data?.owner_name,
    blocks: articleBlocks
  };
}
